import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import ApiIcon from '@mui/icons-material/Api';
import StorageIcon from '@mui/icons-material/Storage';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  autoMatchColumns,
  createMapping,
  fetchMappings,
  fetchMetadata,
  previewCsv,
  processManualUpload,
  testApiConnection,
} from '@src/api/actions/dashboard/dataIntegrations';
import { useAuth } from '@src/context';
import defaultClient from '@src/api/clients/default';
import paths from '@src/paths';
import { useNavigate } from 'react-router-dom';
import { TourOverlay, TourTriggerButton, useTour } from './DataIntegrationWizardTour';

const STEPS_WITH_SETUP = ['Connection Type', 'Setup Instructions', 'Details', 'Map Columns', 'Preview & Activate'];
const STEPS_WITHOUT_SETUP = ['Connection Type', 'Details', 'Map Columns', 'Preview & Activate'];
const STEPS_MANUAL = ['Connection Type', 'Map Columns', 'Preview & Import'];

const INGEST_EMAIL = 'data@leadgurus.com';

const PRIMARY_CONNECTION_TYPES = [
  {
    value: 'email',
    label: 'Automated Email',
    description: 'Your CRM sends a report on a recurring schedule — we process it automatically.',
    icon: <EmailIcon sx={{ fontSize: 44 }} />,
    recommended: true,
  },
  {
    value: 'manual',
    label: 'Manual Upload',
    description: 'Upload a CSV or Excel file yourself — one-click reuploads any time.',
    icon: <UploadFileIcon sx={{ fontSize: 44 }} />,
    recommended: true,
  },
];

const ADVANCED_CONNECTION_TYPES = [
  {
    value: 'api',
    label: 'API Connection',
    description: 'We pull data from your REST API on a schedule.',
    icon: <ApiIcon sx={{ fontSize: 40 }} />,
  },
  {
    value: 'google_sheet',
    label: 'Google Sheet',
    description: 'We pull data from a shared Google spreadsheet.',
    icon: <StorageIcon sx={{ fontSize: 40 }} />,
  },
];

const ALL_CONNECTION_TYPES = [...PRIMARY_CONNECTION_TYPES, ...ADVANCED_CONNECTION_TYPES];

// ─── Step 1: Choose Connection Type ────────────────────────────────

function StepConnectionType({ value, onChange }) {
  const renderCard = (type, primary) => (
    <Card
      key={type.value}
      variant={value === type.value ? 'elevation' : 'outlined'}
      sx={{
        flex: 1,
        minWidth: primary ? 260 : 220,
        border: value === type.value ? '2px solid' : undefined,
        borderColor: value === type.value ? 'primary.main' : undefined,
        position: 'relative',
      }}
    >
      {type.recommended && (
        <Chip
          label="Recommended"
          size="small"
          color="success"
          sx={{ position: 'absolute', top: 8, right: 8, fontSize: 11, height: 20 }}
        />
      )}
      <CardActionArea onClick={() => onChange(type.value)} sx={{ p: primary ? 3.5 : 2.5, textAlign: 'center' }}>
        {type.icon}
        <Typography variant={primary ? 'subtitle1' : 'subtitle2'} fontWeight={600} sx={{ mt: 1 }}>
          {type.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {type.description}
        </Typography>
      </CardActionArea>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        How does your team send backend data?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Most clients use one of the two recommended options. Pick whatever fits your current workflow — you can change this later.
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        data-tour-id="type-picker"
      >
        {PRIMARY_CONNECTION_TYPES.map((t) => renderCard(t, true))}
      </Stack>

      <Accordion
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          '&:before': { display: 'none' },
          borderRadius: 1,
          mt: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="text.secondary">
            More connection types (API, Google Sheet) — for technical users
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {ADVANCED_CONNECTION_TYPES.map((t) => renderCard(t, false))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

// ─── Step 1b: Setup Instructions (email only) ─────────────────────

function StepSetupInstructions({ confirmed, onConfirm }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(INGEST_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Set up your email automation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Before we configure the connection, set up an automated report in your CRM. Here's what we need:
      </Typography>

      <Stack spacing={2.5}>
        <Box
          data-tour-id="setup-email-address"
          sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1 }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1. Send the report to this email address
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1"
              fontWeight={600}
              fontFamily="monospace"
              sx={{ bgcolor: 'white', px: 2, py: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            >
              {INGEST_EMAIL}
            </Typography>
            <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
              {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
            {copied && (
              <Typography variant="caption" color="success.main">Copied!</Typography>
            )}
          </Stack>
        </Box>

        <Box data-tour-id="setup-subject" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            2. Use a consistent subject line
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keep the same subject line every time (e.g., "Daily Lead Report"). We use this to identify your emails automatically.
          </Typography>
        </Box>

        <Box data-tour-id="setup-alltime" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            3. Use all-time data (strongly recommended)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set your report to include <strong>all-time data</strong>, not just the last 7 or 30 days.
            Leads generated months ago may convert today — all-time ensures we capture everything.
            Minimum: last 90 days.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            4. Schedule it daily
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set a daily schedule at a consistent time (e.g., 5:00 AM). We'll check for new data shortly after.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            5. Include as many columns as possible
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The more data you include, the better we can track your full funnel — from lead to appointment to revenue.
            This powers your ROI reporting and helps us optimize your campaigns for the outcomes that matter most.
            You'll map everything in the next step.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip label="Lead / Prospect ID" size="small" color="primary" variant="filled" />
            <Chip label="Created Date" size="small" color="primary" variant="filled" />
            <Chip label="Phone Number" size="small" color="primary" variant="filled" />
            <Chip label="Email Address" size="small" color="primary" variant="filled" />
            <Chip label="Appointment Date" size="small" color="success" variant="outlined" />
            <Chip label="Sold / Close Date" size="small" color="success" variant="outlined" />
            <Chip label="Gross Revenue" size="small" color="success" variant="outlined" />
            <Chip label="Net Revenue" size="small" color="primary" variant="filled" />
            <Chip label="ZIP Code" size="small" color="success" variant="outlined" />
            <Chip label="Demo (Yes/No)" size="small" variant="outlined" />
            <Chip label="Issue (Yes/No)" size="small" variant="outlined" />
            <Chip label="Status / Stage" size="small" variant="outlined" />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" color="primary" variant="filled" sx={{ width: 12, height: 12, mr: 0.5 }} /> Required
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" color="success" variant="outlined" sx={{ width: 12, height: 12, mr: 0.5 }} /> Recommended
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" variant="outlined" sx={{ width: 12, height: 12, mr: 0.5 }} /> Optional
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            6. Use CSV format
          </Typography>
          <Typography variant="body2" color="text.secondary">
            CSV or Excel (.xlsx) attachments work best. Avoid PDF or embedded HTML tables.
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <FormControlLabel
        data-tour-id="setup-confirm"
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => onConfirm(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I've set up the email automation and sent at least one test email to <strong>{INGEST_EMAIL}</strong>
          </Typography>
        }
      />
    </Box>
  );
}

// ─── Step 2: Connection Details ────────────────────────────────────

function StepDetails({ type, config, onChange, clientOptions, selectedClient, onClientChange, isSingleClient, googleServiceAccountEmail }) {
  const update = (field) => (e) => onChange({ ...config, [field]: e.target.value });

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const authType = config.auth_type || 'none';
  const method = (config.method || 'GET').toUpperCase();

  // Parse optional JSON fields for body/headers. Empty means "no value".
  const parseOptionalJson = (raw) => {
    if (raw == null || String(raw).trim() === '') return { value: null, error: null };
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        return { value: null, error: 'Must be a JSON object (e.g. {"key": "value"}).' };
      }
      return { value: parsed, error: null };
    } catch (e) {
      return { value: null, error: `Invalid JSON: ${e.message}` };
    }
  };

  const bodyParsed = parseOptionalJson(config.json_body_text);
  const headersParsed = parseOptionalJson(config.json_headers_text);

  const runApiTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        api_endpoint: config.api_endpoint,
        auth_type: config.auth_type || 'none',
        api_key: config.api_key || '',
        api_key_header: config.api_key_header || 'X-API-Key',
        basic_username: config.basic_username || '',
        method: (config.method || 'GET').toUpperCase(),
      };
      if (headersParsed.value) payload.headers = headersParsed.value;
      if (bodyParsed.value) payload.body = bodyParsed.value;
      const res = await testApiConnection(payload);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({
        ok: false,
        detail: err?.response?.data?.detail || err?.message || 'Test failed.',
      });
    } finally {
      setTesting(false);
    }
  }, [config, headersParsed.value, bodyParsed.value]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {type === 'email'
          ? 'Tell us about the email you set up'
          : type === 'api'
            ? 'Configure your API connection'
            : 'Connect your Google Sheet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {type === 'email'
          ? 'Enter the details of the automated email you configured so we can find and process it.'
          : type === 'api'
            ? 'Provide the endpoint and credentials so we can pull your data.'
            : 'Share your spreadsheet so we can read your data.'}
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 500 }}>
        {isSingleClient ? (
          <TextField
            label="Client"
            value={selectedClient?.name || ''}
            disabled
            helperText="Your account is linked to this client."
          />
        ) : (
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.name || ''}
            value={selectedClient}
            onChange={(_, v) => onClientChange(v)}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            renderInput={(params) => <TextField {...params} label="Client" required />}
          />
        )}

        {type === 'email' && (
          <>
            <TextField
              data-tour-id="details-sender"
              label="Sender Email Address"
              placeholder="reports@theircrm.com"
              value={config.email_sender || ''}
              onChange={update('email_sender')}
              required
              helperText="The 'From' address your CRM uses when it sends the report. Check a real email you've received from it and copy the sender exactly."
            />
            <TextField
              data-tour-id="details-subject"
              label="Email Subject Line"
              placeholder="Daily Lead Report"
              value={config.subject || ''}
              onChange={update('subject')}
              required
              helperText="Must match exactly — any change to the subject in your CRM will cause imports to stop. Copy-paste from a real email rather than typing it."
            />
            <TextField
              label="Attachment Filename Contains"
              placeholder="leads"
              value={config.filename_substring || ''}
              onChange={update('filename_substring')}
              helperText="Optional. Helps us pick the right file when an email has multiple attachments (e.g. 'leads' would match 'leads_2026.csv' and skip 'summary.pdf')."
            />

            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
            <Typography variant="body2" color="text.secondary">
              Set this to run shortly after your CRM sends the email. For example, if your CRM sends at 5:00 AM, set this to 5:15 AM.
            </Typography>
          </>
        )}

        {type === 'api' && (
          <>
            <TextField
              data-tour-id="api-endpoint"
              label="API Endpoint URL"
              placeholder="https://api.example.com/v1/leads"
              value={config.api_endpoint || ''}
              onChange={update('api_endpoint')}
              required
              helperText="Full HTTPS URL we'll call on your schedule. We expect a JSON response — arrays of records or an object with a 'data' / 'leads' / 'items' / 'results' field."
            />

            <FormControl data-tour-id="api-auth">
              <InputLabel>Authentication</InputLabel>
              <Select
                value={authType}
                onChange={update('auth_type')}
                label="Authentication"
              >
                <MenuItem value="none">None (public endpoint)</MenuItem>
                <MenuItem value="bearer">Bearer Token</MenuItem>
                <MenuItem value="api_key">API Key (custom header)</MenuItem>
                <MenuItem value="basic">Basic Auth (username + password)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                {authType === 'none' && "We won't send any auth header. Most APIs require one."}
                {authType === 'bearer' && 'We\'ll send "Authorization: Bearer <your token>" on every request.'}
                {authType === 'api_key' && 'We\'ll send your key in a custom header (default: X-API-Key).'}
                {authType === 'basic' && 'We\'ll send a standard HTTP Basic Authorization header.'}
              </Typography>
            </FormControl>

            {authType === 'bearer' && (
              <TextField
                label="Bearer Token"
                placeholder="eyJhbGciOi... or sk_live_..."
                value={config.api_key || ''}
                onChange={update('api_key')}
                type="password"
                required
                helperText="Stored encrypted. Paste the token exactly — we'll add 'Bearer ' for you."
              />
            )}

            {authType === 'api_key' && (
              <>
                <TextField
                  label="Header Name"
                  placeholder="X-API-Key"
                  value={config.api_key_header || ''}
                  onChange={update('api_key_header')}
                  helperText="Common values: X-API-Key, X-Auth-Token, Api-Key. Check your API's docs."
                />
                <TextField
                  label="API Key"
                  value={config.api_key || ''}
                  onChange={update('api_key')}
                  type="password"
                  required
                  helperText="Stored encrypted. We'll send this as the header value above."
                />
              </>
            )}

            {authType === 'basic' && (
              <>
                <TextField
                  label="Username"
                  value={config.basic_username || ''}
                  onChange={update('basic_username')}
                  required
                />
                <TextField
                  label="Password"
                  value={config.api_key || ''}
                  onChange={update('api_key')}
                  type="password"
                  required
                  helperText="Stored encrypted."
                />
              </>
            )}

            <FormControl data-tour-id="api-method">
              <InputLabel>HTTP Method</InputLabel>
              <Select
                value={method}
                onChange={update('method')}
                label="HTTP Method"
              >
                <MenuItem value="GET">GET (retrieve data)</MenuItem>
                <MenuItem value="POST">POST (send a request body)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Most read-only APIs use GET. Use POST if your API requires a JSON body (e.g. a date filter).
              </Typography>
            </FormControl>

            {method === 'POST' && (
              <TextField
                label="Request Body (JSON)"
                placeholder='{"from_date": "2024-01-01", "limit": 500}'
                value={config.json_body_text || ''}
                onChange={update('json_body_text')}
                multiline
                minRows={3}
                error={!!bodyParsed.error}
                helperText={bodyParsed.error || 'Sent as JSON on every request. Leave empty if your POST endpoint takes no body.'}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            )}

            <Accordion
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
                borderRadius: 1,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Advanced (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    label="Custom Headers (JSON)"
                    placeholder='{"X-Client-Id": "abc123", "Accept": "application/json"}'
                    value={config.json_headers_text || ''}
                    onChange={update('json_headers_text')}
                    multiline
                    minRows={2}
                    error={!!headersParsed.error}
                    helperText={headersParsed.error || "Extra headers on top of your auth header. Don't duplicate Authorization here — it's set from your auth choice above."}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  />

                  <Divider />

                  <Typography variant="body2" color="text.secondary">
                    <strong>Pagination</strong> — only fill in if your API returns results across multiple pages.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Page Param"
                      placeholder="page"
                      value={config.pagination_param || ''}
                      onChange={update('pagination_param')}
                      helperText="Query param for page number."
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Page Size Param"
                      placeholder="page_size"
                      value={config.page_size_param || ''}
                      onChange={update('page_size_param')}
                      helperText="Query param for page size."
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Start Page"
                      type="number"
                      value={config.initial_page ?? ''}
                      onChange={update('initial_page')}
                      helperText="Usually 1, some APIs use 0."
                      sx={{ width: 120 }}
                    />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Box data-tour-id="api-test">
              <Button
                variant="outlined"
                size="small"
                onClick={runApiTest}
                disabled={!config.api_endpoint || testing || !!bodyParsed.error || !!headersParsed.error}
                startIcon={testing ? <CircularProgress size={14} /> : null}
              >
                {testing ? 'Testing…' : 'Test Connection'}
              </Button>
              {testResult && (
                <Alert
                  severity={testResult.ok ? 'success' : 'error'}
                  sx={{ mt: 1.5 }}
                >
                  {testResult.detail || (testResult.ok ? 'Connected.' : 'Connection failed.')}
                  {testResult.status_code ? ` (HTTP ${testResult.status_code})` : ''}
                </Alert>
              )}
            </Box>

            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        {type === 'google_sheet' && (
          <>
            {googleServiceAccountEmail && (
              <Alert data-tour-id="sheet-service-account" severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Before you continue:</strong> share your spreadsheet with this email (Viewer access is enough):
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{ bgcolor: 'white', px: 1.25, py: 0.5, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}
                  >
                    {googleServiceAccountEmail}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(googleServiceAccountEmail)}
                    title="Copy email"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Alert>
            )}
            <TextField
              data-tour-id="sheet-id"
              label="Google Sheet URL or ID"
              placeholder="https://docs.google.com/spreadsheets/d/1A2B3C.../edit"
              value={config.spreadsheet_id || ''}
              onChange={update('spreadsheet_id')}
              required
              helperText="Paste the full URL or just the spreadsheet ID (the long string between /d/ and /edit)."
            />
            <TextField
              data-tour-id="sheet-range"
              label="Sheet & Range"
              placeholder="Sheet1!A:Z"
              value={config.worksheet_range || ''}
              onChange={update('worksheet_range')}
              helperText="Which tab and columns to read. Use the tab name as it appears at the bottom of the spreadsheet, e.g. 'Leads!A:Z' to read all columns A through Z."
            />
            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        <FormControl data-tour-id="details-schedule">
          <InputLabel>Frequency</InputLabel>
          <Select
            value={config.schedule_type || 'daily'}
            onChange={update('schedule_type')}
            label="Frequency"
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="hourly">Every Hour</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Run Time"
          type="time"
          value={config.run_time || '06:00'}
          onChange={update('run_time')}
          helperText={type === 'email'
            ? 'Set this 10–15 minutes after your CRM sends the email.'
            : 'When should we pull new data?'}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Box>
  );
}

// ─── Step 3: Column Mapping ────────────────────────────────────────

const REQUIRED_SET = new Set(['prospect_id', 'created_date', 'net_amount']);
const CONTACT_SET = new Set(['phone', 'email']);
const BOOLEAN_FIELDS = ['demo', 'issue'];
const RECOMMENDED_FIELDS = ['prospect_id', 'created_date', 'phone', 'email', 'appointment_date', 'sold_date', 'gross_amount', 'net_amount', 'zip_code', 'demo', 'issue'];

const BOOL_RULE_TYPES = [
  { value: '', label: 'Not applicable' },
  { value: 'contains', label: 'When a column contains a value' },
  { value: 'not_empty', label: 'When a column is not empty' },
];

const DATE_FIELDS = ['appointment_date', 'sold_date'];

const DATE_RULE_TYPES = [
  { value: '', label: 'Leave empty (not available)' },
  { value: 'copy_mapped', label: 'Always copy from a mapped field' },
  { value: 'copy_csv', label: 'Copy from a CSV column (with optional condition)' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_empty', label: 'Is not empty' },
];

function StepColumnMapping({
  mappings,
  onMappingsChange,
  headers,
  onFileUpload,
  backendFields,
  fieldLabels,
  uploading,
  suggestions,
  transforms,
  onTransformsChange,
  sampleValues,
}) {
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileUpload(file);
  };

  const usedFields = useMemo(() => {
    const used = new Set();
    for (const field of Object.values(mappings)) {
      if (field) used.add(field);
    }
    return used;
  }, [mappings]);

  const hasContact = usedFields.has('phone') || usedFields.has('email');
  const missingRequired = [...REQUIRED_SET].filter((f) => !usedFields.has(f));

  const unmappedDateFields = useMemo(() =>
    DATE_FIELDS.filter((f) => !usedFields.has(f)),
    [usedFields]
  );

  // Mapped backend fields that can serve as date sources
  const mappedDateSources = useMemo(() =>
    ['created_date', 'appointment_date', 'sold_date'].filter((f) => usedFields.has(f)),
    [usedFields]
  );

  const unmappedBoolFields = useMemo(() =>
    BOOLEAN_FIELDS.filter((f) => !usedFields.has(f)),
    [usedFields]
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Map your columns to our fields
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a sample CSV or Excel file (.csv, .xlsx, .xls) from your report. We'll auto-detect your columns and suggest the best matches.
        The more fields you map, the richer your ROI reporting will be.
      </Typography>

      <Button
        data-tour-id="columns-upload"
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        sx={{ mb: 3 }}
      >
        {headers.length ? 'Re-upload Sample File' : 'Upload Sample CSV or Excel'}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        hidden
        onChange={handleFile}
      />

      {headers.length > 0 && (
        <>
          {/* Field coverage chips */}
          <Box
            data-tour-id="columns-coverage"
            sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Field Coverage
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {RECOMMENDED_FIELDS.map((field) => (
                <Chip
                  key={field}
                  label={fieldLabels[field] || field}
                  size="small"
                  color={usedFields.has(field) ? 'success' : (REQUIRED_SET.has(field) || CONTACT_SET.has(field)) ? 'error' : 'default'}
                  variant={usedFields.has(field) ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>

          {missingRequired.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Required: {missingRequired.map((f) => fieldLabels[f] || f).join(', ')}
            </Alert>
          )}
          {!hasContact && (
            <Alert severity="error" sx={{ mb: 2 }}>
              At least one contact field (Phone or Email) must be mapped.
            </Alert>
          )}

          <TableContainer data-tour-id="columns-table" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Your File Column</TableCell>
                  <TableCell>Maps To</TableCell>
                  <TableCell width={80}>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {headers.map((header) => {
                  const current = mappings[header] || '';
                  const suggestion = suggestions?.suggestions?.[header];
                  const confidence = suggestion?.confidence;

                  return (
                    <TableRow key={header}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {header}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={current}
                            onChange={(e) =>
                              onMappingsChange({ ...mappings, [header]: e.target.value })
                            }
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>-- Skip --</em>
                            </MenuItem>
                            {backendFields.map((field) => {
                              const taken = usedFields.has(field) && field !== current;
                              return (
                                <MenuItem key={field} value={field} disabled={taken}>
                                  {fieldLabels[field] || field}
                                  {taken ? ' (already mapped)' : ''}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {confidence != null && (
                          <Chip
                            label={`${Math.round(confidence * 100)}%`}
                            size="small"
                            color={confidence >= 0.9 ? 'success' : confidence >= 0.75 ? 'warning' : 'default'}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Date field rules for unmapped appointment_date / sold_date */}
          {unmappedDateFields.length > 0 && (
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Missing date fields
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your data doesn't have columns mapped for these fields. Copy from another field, optionally with a condition.
              </Typography>
              <Stack spacing={3}>
                {unmappedDateFields.map((field) => {
                  const rule = transforms[`date_${field}`] || {};
                  const ruleType = rule.type || '';

                  return (
                    <Box key={field}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {fieldLabels[field] || field}
                      </Typography>
                      <Stack spacing={1.5}>
                        {/* Rule type */}
                        <FormControl size="small" sx={{ maxWidth: 400 }}>
                          <InputLabel>How to populate</InputLabel>
                          <Select
                            value={ruleType}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`date_${field}`]: { type: e.target.value },
                            })}
                            label="How to populate"
                          >
                            {DATE_RULE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Copy from mapped field (always) */}
                        {ruleType === 'copy_mapped' && (
                          <FormControl size="small" sx={{ maxWidth: 400 }}>
                            <InputLabel>Source field</InputLabel>
                            <Select
                              value={rule.source || ''}
                              onChange={(e) => onTransformsChange({
                                ...transforms,
                                [`date_${field}`]: { ...rule, source: e.target.value },
                              })}
                              label="Source field"
                            >
                              {mappedDateSources
                                .filter((f) => f !== field)
                                .map((f) => (
                                  <MenuItem key={f} value={f}>{fieldLabels[f] || f}</MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        )}

                        {/* Copy from CSV column with optional condition */}
                        {ruleType === 'copy_csv' && (
                          <>
                            <FormControl size="small" sx={{ maxWidth: 400 }}>
                              <InputLabel>Copy date from CSV column</InputLabel>
                              <Select
                                value={rule.sourceColumn || ''}
                                onChange={(e) => onTransformsChange({
                                  ...transforms,
                                  [`date_${field}`]: { ...rule, sourceColumn: e.target.value },
                                })}
                                label="Copy date from CSV column"
                              >
                                {headers.map((h) => (
                                  <MenuItem key={h} value={h}>{h}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Condition (optional) — only set when:
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>CSV column</InputLabel>
                                <Select
                                  value={rule.condColumn || ''}
                                  onChange={(e) => onTransformsChange({
                                    ...transforms,
                                    [`date_${field}`]: { ...rule, condColumn: e.target.value, condOp: rule.condOp || 'equals' },
                                  })}
                                  label="CSV column"
                                >
                                  <MenuItem value=""><em>No condition</em></MenuItem>
                                  {headers.map((h) => (
                                    <MenuItem key={h} value={h}>{h}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {rule.condColumn && (
                                <FormControl size="small" sx={{ minWidth: 130 }}>
                                  <InputLabel>Operator</InputLabel>
                                  <Select
                                    value={rule.condOp || 'equals'}
                                    onChange={(e) => onTransformsChange({
                                      ...transforms,
                                      [`date_${field}`]: { ...rule, condOp: e.target.value },
                                    })}
                                    label="Operator"
                                  >
                                    {CONDITION_OPERATORS.map((op) => (
                                      <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}

                              {rule.condColumn && rule.condOp !== 'not_empty' && (
                                <TextField
                                  size="small"
                                  label="Value"
                                  value={rule.condValue || ''}
                                  onChange={(e) => onTransformsChange({
                                    ...transforms,
                                    [`date_${field}`]: { ...rule, condValue: e.target.value },
                                  })}
                                  sx={{ minWidth: 120 }}
                                />
                              )}
                            </Stack>

                            {rule.sourceColumn && (
                              <Typography variant="caption" color="text.secondary">
                                {rule.condColumn
                                  ? `Set ${fieldLabels[field] || field} = "${rule.sourceColumn}" when "${rule.condColumn}" ${rule.condOp === 'not_empty' ? 'has any value' : `${rule.condOp || 'equals'} "${rule.condValue || ''}"`}`
                                  : `Always set ${fieldLabels[field] || field} from "${rule.sourceColumn}"`
                                }
                              </Typography>
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Boolean field rules for unmapped demo/issue */}
          {unmappedBoolFields.length > 0 && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Outcome fields (Demo / Issue)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your data doesn't have a direct column for these. Set a rule to determine them from other columns.
              </Typography>
              <Stack spacing={3}>
                {unmappedBoolFields.map((field) => {
                  const rule = transforms[`bool_${field}`] || {};
                  const ruleType = rule.type || '';
                  const selectedValues = rule.values || [];
                  const columnValues = (rule.column && sampleValues?.[rule.column]) || [];

                  return (
                    <Box key={field}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {fieldLabels[field] || field}
                      </Typography>
                      <Stack spacing={1.5}>
                        <FormControl size="small" sx={{ maxWidth: 350 }}>
                          <InputLabel>Rule type</InputLabel>
                          <Select
                            value={ruleType}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`bool_${field}`]: { type: e.target.value, column: '', values: [] },
                            })}
                            label="Rule type"
                          >
                            {BOOL_RULE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {(ruleType === 'contains' || ruleType === 'not_empty') && (
                          <FormControl size="small" sx={{ maxWidth: 350 }}>
                            <InputLabel>CSV Column</InputLabel>
                            <Select
                              value={rule.column || ''}
                              onChange={(e) => onTransformsChange({
                                ...transforms,
                                [`bool_${field}`]: { ...rule, column: e.target.value, values: [] },
                              })}
                              label="CSV Column"
                            >
                              {headers.map((h) => (
                                <MenuItem key={h} value={h}>{h}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {ruleType === 'contains' && rule.column && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Select which values mean "{fieldLabels[field] || field}":
                            </Typography>
                            {columnValues.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {columnValues.map((val) => {
                                  const isSelected = selectedValues.includes(val);
                                  return (
                                    <Chip
                                      key={val}
                                      label={val}
                                      size="small"
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      color={isSelected ? 'primary' : 'default'}
                                      onClick={() => {
                                        const next = isSelected
                                          ? selectedValues.filter((v) => v !== val)
                                          : [...selectedValues, val];
                                        onTransformsChange({
                                          ...transforms,
                                          [`bool_${field}`]: { ...rule, values: next },
                                        });
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  );
                                })}
                              </Box>
                            ) : (
                              <TextField
                                size="small"
                                label="Values (comma separated)"
                                placeholder='e.g. Demo, Demo Sold, Appt Confirmed'
                                value={selectedValues.join(', ')}
                                onChange={(e) => {
                                  const vals = e.target.value
                                    .split(',')
                                    .map((v) => v.trim())
                                    .filter(Boolean);
                                  onTransformsChange({
                                    ...transforms,
                                    [`bool_${field}`]: { ...rule, values: vals },
                                  });
                                }}
                                sx={{ maxWidth: 400 }}
                              />
                            )}
                            {selectedValues.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {selectedValues.length} value{selectedValues.length !== 1 ? 's' : ''} selected
                              </Typography>
                            )}
                          </Box>
                        )}

                        {ruleType === 'not_empty' && rule.column && (
                          <Typography variant="body2" color="text.secondary">
                            {fieldLabels[field] || field} will be set to Yes when "{rule.column}" has any value.
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Step 4: Preview & Activate ────────────────────────────────────

function StepPreview({ previewData, loading, error, onRunPreview }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Running preview...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!previewData) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Almost there — let's verify everything looks right
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We'll process your sample file with the mappings you configured and show you
          exactly how the data will appear. If something looks off, you can go back and adjust.
        </Typography>
        <Button data-tour-id="preview-run" variant="contained" onClick={onRunPreview}>
          Run Preview
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Preview results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review the parsed data below. If everything looks correct, activate the connection — we'll start importing automatically on schedule.
      </Typography>

      <Stack data-tour-id="preview-counts" direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700}>
            {previewData.total_rows}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Rows
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="success.main">
            {previewData.success_count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Parsed OK
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="error.main">
            {previewData.error_count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Errors
          </Typography>
        </Card>
      </Stack>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Sample Rows (first {previewData.preview_rows.length})
      </Typography>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              {Object.keys(previewData.preview_rows[0]?.parsed || previewData.preview_rows[0]?.raw || {})
                .slice(0, 8)
                .map((key) => (
                  <TableCell key={key}>{key}</TableCell>
                ))}
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewData.preview_rows.map((row) => {
              const displayData = row.parsed || row.raw;
              const keys = Object.keys(displayData || {}).slice(0, 8);
              return (
                <TableRow key={row.row_number}>
                  <TableCell>{row.row_number}</TableCell>
                  {keys.map((key) => (
                    <TableCell key={key} sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(displayData[key] ?? '')}
                    </TableCell>
                  ))}
                  <TableCell>
                    {row.error ? (
                      <Chip label="Error" color="error" size="small" />
                    ) : (
                      <Chip label="OK" color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main Wizard ───────────────────────────────────────────────────

export default function DataIntegrationWizardPage() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const user = state?.user;

  // ── Resolve accessible clients from membership context ──
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (!user) return;

    // Always fetch the user's accessible clients from the API.
    // The endpoint is scoped by limit_to_user_clients so non-staff
    // users only see their own clients (direct + parent-company children).
    defaultClient
      .request({ url: 'dashboard/clients/?fields=id,name,slug&page_size=500', method: 'GET' })
      .then((res) => {
        const rows = Array.isArray(res?.data?.results)
          ? res.data.results
          : Array.isArray(res?.data) ? res.data : [];
        setClientOptions(rows);
        // Auto-select when there's exactly one client
        if (rows.length === 1) setSelectedClient(rows[0]);
      })
      .catch(() => setClientOptions([]));
  }, [user]);

  const isStaff = user?.is_staff || user?.is_superuser;
  const isSingleClient = !isStaff && clientOptions.length === 1 && selectedClient;

  const [activeStep, setActiveStep] = useState(0);
  const [connectionType, setConnectionType] = useState('');
  const [setupConfirmed, setSetupConfirmed] = useState(false);
  const [config, setConfig] = useState({ schedule_type: 'daily', run_time: '06:00' });

  // Manual upload has no credentials, no schedule — shorter flow.
  // Email gets a setup instructions step. Everything else runs standard.
  const isManual = connectionType === 'manual';
  const hasSetupStep = connectionType === 'email';
  const steps = isManual
    ? STEPS_MANUAL
    : hasSetupStep ? STEPS_WITH_SETUP : STEPS_WITHOUT_SETUP;
  const STEP_SETUP = hasSetupStep ? 1 : -1;
  // Manual has no Details step. Column picker lives on Map Columns step.
  const STEP_DETAILS = isManual ? -1 : (hasSetupStep ? 2 : 1);
  const STEP_COLUMNS = isManual ? 1 : (hasSetupStep ? 3 : 2);
  const STEP_PREVIEW = isManual ? 2 : (hasSetupStep ? 4 : 3);
  const [columnMappings, setColumnMappings] = useState({});
  const [dateTransforms, setDateTransforms] = useState({});
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [sampleValues, setSampleValues] = useState({});
  const [suggestions, setSuggestions] = useState(null);
  const [sampleFile, setSampleFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    fetchMetadata()
      .then((res) => setMetadata(res.data))
      .catch(() => {});
  }, []);

  const backendFields = metadata?.backend_fields || [];
  const fieldLabels = metadata?.field_labels || {};

  const handleFileUpload = useCallback(
    async (file) => {
      setUploading(true);
      setSampleFile(file);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await autoMatchColumns(formData);
        const data = res.data;
        setCsvHeaders(
          Object.keys(data.suggestions || {}).concat(data.unmapped || [])
        );
        setSuggestions(data);
        setSampleValues(data.sample_values || {});

        // Auto-populate column mappings from suggestions
        const auto = {};
        for (const [header, info] of Object.entries(data.suggestions || {})) {
          if (info.field) auto[header] = info.field;
        }
        setColumnMappings(auto);
      } catch (err) {
        console.error('Auto-match failed:', err);
      } finally {
        setUploading(false);
      }
    },
    []
  );

  const handleRunPreview = useCallback(async () => {
    if (!sampleFile) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      // Build clean column_mappings (skip empty values)
      const cleanMappings = {};
      for (const [header, field] of Object.entries(columnMappings)) {
        if (field) cleanMappings[header] = field;
      }

      const formData = new FormData();
      formData.append('file', sampleFile);
      formData.append('column_mappings', JSON.stringify(cleanMappings));

      const res = await previewCsv(formData);
      setPreviewData(res.data);
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || err?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  }, [sampleFile, columnMappings]);

  const handleActivate = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    const cleanMappings = {};
    for (const [header, field] of Object.entries(columnMappings)) {
      if (field) cleanMappings[header] = field;
    }

    // Build transform_params from date rules + boolean rules
    const transformations = [];
    for (const [key, value] of Object.entries(dateTransforms)) {
      if (key.startsWith('bool_')) {
        // Boolean field rule (demo/issue)
        const field = key.replace('bool_', '');
        const rule = value;
        if (!rule.type || !rule.column) continue;

        if (rule.type === 'contains' && rule.values?.length) {
          for (const matchVal of rule.values) {
            transformations.push({
              set: field,
              to: true,
              when: { all: [{ field: [rule.column], eq: matchVal }] },
            });
          }
        } else if (rule.type === 'not_empty') {
          transformations.push({
            set: field,
            to: true,
            when: { all: [{ field: [rule.column], not_empty: true }] },
          });
        }
      } else if (key.startsWith('date_')) {
        // Date field rule (appointment_date/sold_date)
        const field = key.replace('date_', '');
        const rule = value;
        if (!rule.type) continue;

        if (rule.type === 'copy_mapped' && rule.source) {
          // Always copy from another mapped backend field
          transformations.push({
            set: field,
            to: { field: [rule.source] },
          });
        } else if (rule.type === 'copy_csv' && rule.sourceColumn) {
          // Copy from a CSV column, optionally with a condition
          const entry = {
            set: field,
            to: { field: [rule.sourceColumn] },
          };
          if (rule.condColumn) {
            if (rule.condOp === 'not_empty') {
              entry.when = { all: [{ field: [rule.condColumn], not_empty: true }] };
            } else if (rule.condOp === 'contains') {
              entry.when = { all: [{ field: [rule.condColumn], contains: rule.condValue }] };
            } else {
              // equals (default)
              entry.when = { all: [{ field: [rule.condColumn], eq: rule.condValue }] };
            }
          }
          transformations.push(entry);
        }
      }
    }
    const transformParams = transformations.length > 0 ? { transformations } : {};

    const payload = {
      client: selectedClient?.id,
      schedule_type: config.schedule_type || 'daily',
      run_time: config.run_time || '06:00',
      column_mappings: cleanMappings,
      transform_params: transformParams,
    };

    if (connectionType === 'email') {
      payload.email_sender = config.email_sender;
      payload.subject = config.subject;
      payload.filename_substring = config.filename_substring || '';
    } else if (connectionType === 'api') {
      payload.api_endpoint = config.api_endpoint;
      payload.api_key = config.api_key || '';

      const authType = config.auth_type || 'none';
      const method = (config.method || 'GET').toUpperCase();
      const nextTransform = { ...(payload.transform_params || {}) };

      if (method !== 'GET') nextTransform.method = method;
      if (authType !== 'none') {
        nextTransform.auth_type = authType;
        if (authType === 'api_key' && config.api_key_header) {
          nextTransform.api_key_header = config.api_key_header;
        }
        if (authType === 'basic' && config.basic_username) {
          nextTransform.basic_username = config.basic_username;
        }
      }
      if (Object.keys(nextTransform).length > 0) {
        payload.transform_params = nextTransform;
      }

      // Parse body + headers JSON text inputs; silently ignore if invalid
      // (the UI blocks submission via the Test Connection disabled state,
      // but we also guard here so a bad parse never clobbers the payload).
      const parseJsonSilent = (raw) => {
        if (raw == null || String(raw).trim() === '') return null;
        try {
          const parsed = JSON.parse(raw);
          return typeof parsed === 'object' && !Array.isArray(parsed) && parsed ? parsed : null;
        } catch {
          return null;
        }
      };
      const body = parseJsonSilent(config.json_body_text);
      const headers = parseJsonSilent(config.json_headers_text);
      if (body && method !== 'GET') payload.json_body_template = body;
      if (headers) payload.json_headers = headers;

      if (config.pagination_param) payload.pagination_param = config.pagination_param;
      if (config.page_size_param) payload.page_size_param = config.page_size_param;
      if (config.initial_page !== undefined && config.initial_page !== '') {
        const parsed = parseInt(config.initial_page, 10);
        if (!Number.isNaN(parsed)) payload.initial_page = parsed;
      }
    } else if (connectionType === 'google_sheet') {
      payload.spreadsheet_id = config.spreadsheet_id;
      payload.worksheet_range = config.worksheet_range || '';
    }

    try {
      if (connectionType === 'manual') {
        // Manual: skip mapping CRUD and just run the import now.
        const form = new FormData();
        form.append('file', sampleFile);
        form.append('client', selectedClient?.id);
        form.append('column_mappings', JSON.stringify(cleanMappings));
        form.append('transform_params', JSON.stringify(transformParams));
        await processManualUpload(form);
      } else {
        await createMapping(connectionType, payload);
      }
      navigate(paths.dataIntegrations.health);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === 'object'
          ? JSON.stringify(err.response.data)
          : err?.message || 'Failed to create integration.');
      setSaveError(detail);
    } finally {
      setSaving(false);
    }
  }, [connectionType, config, columnMappings, dateTransforms, selectedClient, sampleFile, navigate]);

  const canProceed = useMemo(() => {
    if (activeStep === 0) return !!connectionType;
    if (activeStep === STEP_SETUP) return setupConfirmed;
    if (activeStep === STEP_DETAILS) {
      if (!selectedClient) return false;
      if (connectionType === 'email') return !!(config.email_sender && config.subject);
      if (connectionType === 'api') return !!config.api_endpoint;
      if (connectionType === 'google_sheet') return !!config.spreadsheet_id;
      return true;
    }
    if (activeStep === STEP_COLUMNS) {
      // Manual flow picks the client on this step; everything else picks
      // it earlier in Details.
      if (connectionType === 'manual' && !selectedClient) return false;
      const mapped = new Set(Object.values(columnMappings).filter(Boolean));
      const hasRequired = mapped.has('prospect_id') && mapped.has('created_date') && mapped.has('net_amount');
      const hasContact = mapped.has('phone') || mapped.has('email');
      return hasRequired && hasContact;
    }
    return true;
  }, [activeStep, connectionType, config, selectedClient, columnMappings, setupConfirmed, STEP_SETUP, STEP_DETAILS, STEP_COLUMNS]);

  const tour = useTour({ activeStep, connectionType, isManual, hasSetupStep });

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(paths.dataIntegrations.health)}
        >
          Back to Integrations
        </Button>
        <TourTriggerButton tour={tour} />
      </Stack>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Connect Your Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Set up a connection to automatically import your backend data.
      </Typography>

      <TourOverlay tour={tour} />

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && (
          <StepConnectionType value={connectionType} onChange={setConnectionType} />
        )}
        {activeStep === STEP_SETUP && (
          <StepSetupInstructions
            confirmed={setupConfirmed}
            onConfirm={setSetupConfirmed}
          />
        )}
        {activeStep === STEP_DETAILS && (
          <StepDetails
            type={connectionType}
            config={config}
            onChange={setConfig}
            clientOptions={clientOptions || []}
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            isSingleClient={isSingleClient}
            googleServiceAccountEmail={metadata?.google_service_account_email || ''}
          />
        )}
        {activeStep === STEP_COLUMNS && (
          <>
            {/* Manual upload picks the client here since there's no Details step. */}
            {isManual && !isSingleClient && (
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={clientOptions}
                  getOptionLabel={(o) => o.name || ''}
                  value={selectedClient}
                  onChange={(_, v) => setSelectedClient(v)}
                  isOptionEqualToValue={(option, val) => option.id === val?.id}
                  renderInput={(p) => <TextField {...p} label="Client" required helperText="Which client is this data for?" />}
                  sx={{ maxWidth: 500 }}
                />
              </Box>
            )}
            {isManual && isSingleClient && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Importing for <strong>{selectedClient?.name}</strong>.
              </Alert>
            )}
            <StepColumnMapping
              mappings={columnMappings}
              onMappingsChange={setColumnMappings}
              headers={csvHeaders}
              onFileUpload={handleFileUpload}
              backendFields={backendFields}
              fieldLabels={fieldLabels}
              uploading={uploading}
              suggestions={suggestions}
              transforms={dateTransforms}
              onTransformsChange={setDateTransforms}
              sampleValues={sampleValues}
            />
          </>
        )}
        {activeStep === STEP_PREVIEW && (
          <StepPreview
            previewData={previewData}
            loading={previewLoading}
            error={previewError}
            onRunPreview={handleRunPreview}
          />
        )}
      </Card>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Button
          disabled={activeStep === 0}
          onClick={() => setActiveStep((s) => s - 1)}
        >
          Back
        </Button>
        <Box>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              disabled={!canProceed}
              onClick={() => setActiveStep((s) => s + 1)}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              disabled={saving}
              onClick={handleActivate}
            >
              {saving
                ? (isManual ? 'Importing...' : 'Activating...')
                : (isManual ? 'Import File' : 'Activate Connection')}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
